import { type Dispatch } from "redux";
import { getAxiosError } from "../../../../../utils/axiosError";
import { type BasePerformanceRequestFilterParams } from "../../../Common/models";
import DataService from "../../services/assessmentsDataService";
import {
  setQuestionResponsesData,
  setEngagementLineData,
  setEngagementFunnelData,
  setEngagementQuestionsData,
  reqQuestionResponsesPeople,
  setQuestionResponsesPeople,
  setQuestionResponsesPeopleError,
  reqQuestionResponsesData,
  reqEngagementLineData,
  reqEngagementFunnelData,
  reqEngagementQuestionsData,
  setEngagementError,
  setEngagementFunnelError,
  setEngagementQuestionsError,
  setQuestionResponsesError,
  reqPeopleDetails,
  setPeopleDetails,
  setPeopleDetailsError,
} from "../slices/assessmentPerformanceSlice";
import { sendExportRequest } from "../../../Common/utils/performanceUtils";
import { EmptyAssessmentEngagementFunnelData } from "../../types/mocks";
import { type QuestionResponsesData } from "../../types/state";

export const fetchQuestionResponsesData = (
  assessmentId: number,
  questionId: string,
  filterParams: BasePerformanceRequestFilterParams,
) => {
  return async (dispatch: Dispatch) => {
    dispatch(reqQuestionResponsesData());
    try {
      const data = await DataService.getQuestionResponses(assessmentId, questionId, filterParams);
      // Question responses data now returns as an array of objects,
      // this guards against that in case we change back to returning an object
      const dataObj: QuestionResponsesData = Array.isArray(data) ? data[0] : data;
      dispatch(
        setQuestionResponsesData({
          data: dataObj,
        }),
      );
    } catch (err) {
      if (err.code === "ERR_CANCELED") {
        // intended for when the user switches questions, as the previous request will throw an error when aborted. This allows us to give a better visual transition when that occurs.
        dispatch(reqQuestionResponsesData());
      } else {
        const errorMessage = getAxiosError(err);
        dispatch(setQuestionResponsesError({ errorMessage }));
      }
    }
  };
};

export const fetchEngagementLineData = (entityId: number, filterParams: BasePerformanceRequestFilterParams) => {
  return async (dispatch: Dispatch) => {
    dispatch(reqEngagementLineData());
    try {
      const data = await DataService.getAssessmentEngagementLine(entityId, filterParams);

      dispatch(
        setEngagementLineData({
          data,
        }),
      );
    } catch (err) {
      // Cancellation token error
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setEngagementError({ errorMessage }));
      }
    }
  };
};

export const fetchEngagementFunnelData = (entityId: number, filterParams: BasePerformanceRequestFilterParams) => {
  return async (dispatch: Dispatch) => {
    dispatch(reqEngagementFunnelData());
    try {
      let data = await DataService.getAssessmentEngagementFunnel(entityId, filterParams);
      data = {
        ...EmptyAssessmentEngagementFunnelData,
        ...data,
      };

      dispatch(
        setEngagementFunnelData({
          data,
        }),
      );
    } catch (err) {
      // Cancellation token error
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setEngagementFunnelError({ errorMessage }));
      }
    }
  };
};

export const fetchEngagementQuestionsData = (entityId: number, filterParams: BasePerformanceRequestFilterParams) => {
  return async (dispatch: Dispatch) => {
    dispatch(reqEngagementQuestionsData());
    try {
      const data = await DataService.getAssessmentEngagementQuestions(entityId, filterParams);

      dispatch(
        setEngagementQuestionsData({
          data,
        }),
      );
    } catch (err) {
      // Cancellation token error
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setEngagementQuestionsError({ errorMessage }));
      }
    }
  };
};

export const fetchEngagementQuestionsPeopleData = (
  entityId: number,
  questionId: string,
  answerId: string,
  filterParams: BasePerformanceRequestFilterParams = {},
) => {
  return async (dispatch: Dispatch) => {
    dispatch(reqQuestionResponsesPeople());
    try {
      const { data } = await DataService.getAssessmentEngagementQuestionsPeople(
        entityId,
        questionId,
        answerId,
        filterParams,
      );

      dispatch(setQuestionResponsesPeople({ data }));
    } catch (err) {
      // Request was aborted manually, no need to change anything
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setQuestionResponsesPeopleError({ errorMessage }));
      }
    }
  };
};

export const fetchPeopleDetails = (assessmentId: number, filterParams: BasePerformanceRequestFilterParams) => {
  return async (dispatch: Dispatch) => {
    dispatch(reqPeopleDetails());

    try {
      const results = await DataService.getAssessmentPeopleDetails(assessmentId, filterParams);

      const resultsWithId = results.map((item) => {
        return {
          ...item,
          id: `${item.UserId}${item.CompletionDate}`,
        };
      });
      dispatch(setPeopleDetails({ data: resultsWithId }));
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setPeopleDetailsError({ errorMessage }));
      }
    }
  };
};

export const doExport = (
  assessmentId: number,
  filterParams: BasePerformanceRequestFilterParams,
  assessmentTitle: string,
) => {
  let abortController = new AbortController();
  return sendExportRequest(
    (abortController) => DataService.getExport(assessmentId, filterParams),
    assessmentTitle,
    abortController,
  );
};
